import { useState, useCallback } from "react";
import { MessageSquare, Plus, Mail, MailOpen, Send } from "lucide-react";
import { Button, cn } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import {
  useMessages,
  useSendMessage,
  useMarkMessageRead,
  useAdminUsers,
} from "@/hooks/admin/useOperations";
import type { Message, SendMessageInput } from "@/lib/admin-api/operations";

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatDateTime(d: string): string {
  return new Date(d).toLocaleString("fr-SN", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Send message modal
// ---------------------------------------------------------------------------

interface SendMessageModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly isSaving: boolean;
  readonly onSend: (input: SendMessageInput) => Promise<void>;
  readonly adminUsers: ReadonlyArray<{ readonly id: string; readonly full_name: string }>;
}

function SendMessageModal({
  open,
  onClose,
  isSaving,
  onSend,
  adminUsers,
}: SendMessageModalProps) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipientIds, setRecipientIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setSubject("");
    setBody("");
    setRecipientIds([]);
    setError(null);
    onClose();
  }, [onClose]);

  const toggleRecipient = useCallback((id: string) => {
    setRecipientIds((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!subject.trim()) {
      setError("L'objet est requis.");
      return;
    }
    if (!body.trim()) {
      setError("Le contenu est requis.");
      return;
    }
    if (recipientIds.length === 0) {
      setError("Sélectionnez au moins un destinataire.");
      return;
    }
    setError(null);
    try {
      await onSend({ subject: subject.trim(), body: body.trim(), recipient_ids: recipientIds });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [subject, body, recipientIds, onSend, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Nouveau message"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving} className="gap-2">
            <Send className="h-4 w-4" />
            {isSaving ? "Envoi…" : "Envoyer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">
            Destinataires <span className="text-red-500">*</span>
          </label>
          <div className="max-h-36 overflow-y-auto rounded-md border border-stone-200 p-2">
            {adminUsers.length === 0 ? (
              <p className="py-2 text-center text-xs text-stone-400">
                Chargement des administrateurs…
              </p>
            ) : (
              <div className="space-y-1">
                {adminUsers.map((user) => (
                  <label
                    key={user.id}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-stone-50"
                  >
                    <input
                      type="checkbox"
                      checked={recipientIds.includes(user.id)}
                      onChange={() => toggleRecipient(user.id)}
                      className="rounded border-stone-300"
                    />
                    {user.full_name}
                  </label>
                ))}
              </div>
            )}
          </div>
          {recipientIds.length > 0 && (
            <p className="mt-1 text-xs text-stone-500">
              {recipientIds.length} destinataire{recipientIds.length > 1 ? "s" : ""} sélectionné
              {recipientIds.length > 1 ? "s" : ""}
            </p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">
            Objet <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="Objet du message"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">
            Message <span className="text-red-500">*</span>
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="Contenu du message…"
          />
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Message row
// ---------------------------------------------------------------------------

interface MessageRowProps {
  readonly message: Message;
  readonly onMarkRead: (id: string) => void;
}

function MessageRow({ message, onMarkRead }: MessageRowProps) {
  const [expanded, setExpanded] = useState(false);

  const handleToggle = useCallback(() => {
    setExpanded((prev) => !prev);
    if (!message.is_read) {
      onMarkRead(message.id);
    }
  }, [message.id, message.is_read, onMarkRead]);

  return (
    <div
      className={cn(
        "rounded-lg border transition-colors",
        message.is_read ? "border-stone-200 bg-white" : "border-amber-200 bg-amber-50/30",
      )}
    >
      <button
        onClick={handleToggle}
        className="flex w-full items-start gap-3 p-4 text-left"
      >
        <div className="mt-0.5 shrink-0 text-stone-400">
          {message.is_read ? (
            <MailOpen className="h-4 w-4" />
          ) : (
            <Mail className="h-4 w-4 text-amber-600" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className={cn("text-sm", message.is_read ? "text-stone-700" : "font-semibold text-stone-900")}>
              {message.subject}
            </p>
            <span className="shrink-0 text-xs text-stone-400">
              {formatDateTime(message.sent_at)}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-stone-500">{message.sender_name}</p>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-stone-100 px-4 pb-4 pt-3">
          <p className="whitespace-pre-wrap text-sm text-stone-700">{message.body}</p>
          {message.recipients.length > 0 && (
            <p className="mt-3 text-xs text-stone-400">
              Destinataires : {message.recipients.join(", ")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

type Folder = "inbox" | "sent";

export default function Messages() {
  const [folder, setFolder] = useState<Folder>("inbox");
  const [page, setPage] = useState(1);
  const [showCompose, setShowCompose] = useState(false);

  const { data, isLoading } = useMessages({ page, limit: 25, folder });
  const { data: adminUsersData } = useAdminUsers({ limit: 100 });
  const sendMutation = useSendMessage();
  const markReadMutation = useMarkMessageRead();

  const messages = data?.messages ?? [];
  const pagination = data?.pagination;
  const unreadCount = data?.unread_count ?? 0;
  const adminUsers = adminUsersData?.users ?? [];

  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  const sentToday = messages.filter(
    (m) => m.sent_at >= startOfDay,
  ).length;

  const handleTabChange = useCallback((tab: Folder) => {
    setFolder(tab);
    setPage(1);
  }, []);

  const handleSend = useCallback(
    async (input: SendMessageInput) => {
      await sendMutation.mutateAsync(input);
    },
    [sendMutation],
  );

  const handleMarkRead = useCallback(
    (id: string) => {
      markReadMutation.mutate(id);
    },
    [markReadMutation],
  );

  const totalPages = pagination ? Math.max(1, Math.ceil(pagination.total / pagination.limit)) : 1;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <MessageSquare className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Messagerie interne
              </h1>
              <p className="text-sm text-stone-500">Communication entre administrateurs</p>
            </div>
          </div>
          <Button onClick={() => setShowCompose(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Nouveau message
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <KPICard
            label="Non lus"
            value={unreadCount}
            icon={<Mail className="h-4 w-4" />}
          />
          <KPICard label="Envoyés aujourd'hui" value={sentToday} />
          <KPICard label="Total messages" value={pagination?.total ?? messages.length} />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 rounded-lg border border-stone-200 bg-white p-1 w-fit">
          <button
            onClick={() => handleTabChange("inbox")}
            className={cn(
              "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
              folder === "inbox"
                ? "bg-amber-600 text-white"
                : "text-stone-600 hover:bg-stone-50",
            )}
          >
            Reçus
            {unreadCount > 0 && folder !== "inbox" && (
              <span className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-600 text-xs text-white">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => handleTabChange("sent")}
            className={cn(
              "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
              folder === "sent"
                ? "bg-amber-600 text-white"
                : "text-stone-600 hover:bg-stone-50",
            )}
          >
            Envoyés
          </button>
        </div>

        {/* Message list */}
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-16 animate-pulse rounded-lg border border-stone-200 bg-stone-50"
              />
            ))}
          </div>
        ) : messages.length === 0 ? (
          <div className="rounded-lg border border-stone-200 bg-white py-12 text-center">
            <p className="text-sm text-stone-400">Aucun message.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {messages.map((message) => (
              <MessageRow
                key={message.id}
                message={message}
                onMarkRead={handleMarkRead}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination && totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-stone-500">
              Page {pagination.page} / {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Précédent
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
      </div>

      <SendMessageModal
        open={showCompose}
        onClose={() => setShowCompose(false)}
        isSaving={sendMutation.isPending}
        onSend={handleSend}
        adminUsers={adminUsers}
      />
    </AdminLayout>
  );
}
